import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import AmexAccountVerificationActionRequiredInnerContent from 'src/components/AmexAccountVerificationActionRequiredBox/AmexAccountVerificationActionRequiredInnerContent'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgStatusSummaryActionRequiredRed from 'src/components/Svgs/SvgStatusSummaryActionRequiredRed'
import { AmexCardHolderTypes, AmexCardTypes, IssuerCodes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { accountOverviewUrl } from 'src/navigation/urls'
import { getCreditCardProperties } from 'src/redux/actions/data/creditCards.actions'
import { css } from 'styled-components'

interface AmexAccountVerificationActionRequiredBoxProps {
  creditCard: CreditCardListModel
}

const AmexAccountVerificationActionRequiredBox: React.FC<
  AmexAccountVerificationActionRequiredBoxProps
> = (props): React.ReactElement => {
  const dispatch = useBackgroundDispatch()

  useEffect((): void => {
    dispatch(getCreditCardProperties(props.creditCard.id))
  }, [])

  const innerContent = (
    creditCard: CreditCardListModel,
  ): React.ReactElement => {
    const cardProperties = creditCard.cardProperties
    const celWhitelisted = creditCard.issuer === IssuerCodes.AmexCel

    const emailDeliveryMessage = (
      <Trns source={'This email may take up to 10 minutes to be delivered'} />
    )
    const dontKnowYourAmexLoginMessage = (
      <Trns
        source={
          "Don't know your Amex login? ${clickHere} to recover your credentials"
        }
        expressions={{
          clickHere: (
            <HoverableLink
              size={HoverableLinkSizes.Small}
              underline
              regular
              href={
                'https://www.americanexpress.com/en-us/account/userid/recover'
              }
              ariaLabel={'Click here'}
            >
              <Trns source={'Click here'} />
            </HoverableLink>
          ),
        }}
      />
    )

    const justAFewMoreStepsMessage = (
      <Trns
        source={'Just a few more steps to complete account registration:'}
      />
    )
    const goBackToApp = (key: number): React.ReactElement => {
      return (
        <Trns
          key={key}
          source={"${goBackToApp} - you're now ready to create virtual cards!"}
          expressions={{
            goBackToApp: (
              <b>
                <Trns
                  source={'Go back to ${appName}'}
                  expressions={{
                    appName: <AppName />,
                  }}
                />
              </b>
            ),
          }}
        />
      )
    }

    if (celWhitelisted) {
      return (
        <AmexAccountVerificationActionRequiredInnerContent
          subtitle={justAFewMoreStepsMessage}
          steps={[
            <Trns
              key={0}
              source={
                '${checkYourInbox} for a verification email from ${appName}.'
              }
              expressions={{
                checkYourInbox: (
                  <b>
                    <Trns source={'Check your inbox'} />
                  </b>
                ),
                appName: <AppName />,
              }}
            />,
            <Trns
              key={1}
              source={
                '${clickTheVerificationLink}. Follow the link in the email to verify your registration with American Express.'
              }
              expressions={{
                clickTheVerificationLink: (
                  <b>
                    <Trns source={'Click the verification link'} />
                  </b>
                ),
              }}
            />,
            goBackToApp(2),
          ]}
        />
      )
    } else {
      if (
        cardProperties?.cardType === AmexCardTypes.Business &&
        cardProperties?.cardHolderType === AmexCardHolderTypes.Secondary
      ) {
        return (
          <AmexAccountVerificationActionRequiredInnerContent
            subtitle={
              <>
                <div
                  css={css`
                    margin-bottom: 20px;
                  `}
                >
                  <Trns
                    source={
                      'The primary cardholder has received an email from American Express to complete verification of your card.'
                    }
                  />
                </div>
                <Trns source={'Ask them to follow the steps below:'} />
              </>
            }
            steps={[
              <Trns
                key={0}
                source={
                  '${checkTheirInbox} for a verification email from American Express.'
                }
                expressions={{
                  checkTheirInbox: (
                    <b>
                      <Trns source={'Check their inbox'} />
                    </b>
                  ),
                }}
              />,
              <Trns
                key={1}
                source={
                  '${clickTheVerificationLink}. The primary cardholder should follow the link in the email to log into American Express and verify your account.'
                }
                expressions={{
                  clickTheVerificationLink: (
                    <b>
                      <Trns source={'Click the verification link'} />
                    </b>
                  ),
                }}
              />,
            ]}
            endMessage={
              <Trns
                source={
                  'Once verified, you can start creating virtual cards in ${appName}!'
                }
                expressions={{
                  appName: <AppName />,
                }}
              />
            }
            takeNoteBullets={[
              <div key={0}>{emailDeliveryMessage}</div>,
              <div key={1}>
                <Trns
                  source={
                    'Visit our ${resourceCenter} for detailed instructions'
                  }
                  expressions={{
                    resourceCenter: (
                      <ResourceCenterLink
                        size={HoverableLinkSizes.Small}
                        underline
                        regular
                        analytics={{
                          event:
                            'account detail - alert component helpcenter link - text link press',
                          properties: {
                            interaction: 'text link press',
                            intent: 'alert component helpcenter link',
                            screen: accountOverviewUrl(':id'),
                          },
                        }}
                      />
                    ),
                  }}
                />
              </div>,
            ]}
          />
        )
      } else if (
        cardProperties?.cardType === AmexCardTypes.Corporate ||
        (cardProperties?.cardType === AmexCardTypes.Business &&
          cardProperties?.cardHolderType === AmexCardHolderTypes.Primary)
      ) {
        return (
          <AmexAccountVerificationActionRequiredInnerContent
            subtitle={justAFewMoreStepsMessage}
            steps={[
              <Trns
                key={0}
                source={
                  '${checkYourInbox} for a verification email from American Express.'
                }
                expressions={{
                  checkYourInbox: (
                    <b>
                      <Trns source={'Check your inbox'} />
                    </b>
                  ),
                }}
              />,
              <Trns
                key={1}
                source={
                  '${clickTheVerificationLink}. Follow the link in the email to log into American Express and verify your account.'
                }
                expressions={{
                  clickTheVerificationLink: (
                    <b>
                      <Trns source={'Click the verification link'} />
                    </b>
                  ),
                }}
              />,
              goBackToApp(2),
            ]}
            takeNoteBullets={[
              <div key={0}>{emailDeliveryMessage}</div>,
              <div key={1}>{dontKnowYourAmexLoginMessage}</div>,
              <div key={2}>
                <Trns
                  source={
                    'Visit our ${resourceCenter} for detailed instructions'
                  }
                  expressions={{
                    resourceCenter: (
                      <ResourceCenterLink
                        size={HoverableLinkSizes.Small}
                        underline
                        regular
                        analytics={{
                          event:
                            'account detail - alert component helpcenter link - text link press',
                          properties: {
                            interaction: 'text link press',
                            intent: 'alert component helpcenter link',
                            screen: accountOverviewUrl(':id'),
                          },
                        }}
                      />
                    ),
                  }}
                />
              </div>,
            ]}
          />
        )
      } else {
        return (
          <AmexAccountVerificationActionRequiredInnerContent
            subtitle={
              <Trns
                source={
                  "Before you can use your Amex to create virtual cards in ${appName}, there's a few more steps to complete:"
                }
                expressions={{
                  appName: <AppName />,
                }}
              />
            }
            steps={[
              <Trns
                key={0}
                source={
                  "${findThePrimaryCardholder}. If you set up your company's Amex card program, this is likely you. Only the primary cardholder can complete verification."
                }
                expressions={{
                  findThePrimaryCardholder: (
                    <b>
                      <Trns source={'Find the primary cardholder'} />
                    </b>
                  ),
                }}
              />,
              <Trns
                key={1}
                source={
                  '${checkEmail}. The primary cardholder should check their inbox for a verification email from Amex.'
                }
                expressions={{
                  checkEmail: (
                    <b>
                      <Trns source={'Check email'} />
                    </b>
                  ),
                }}
              />,
              <Trns
                key={2}
                source={
                  '${clickTheVerificationLink}. The primary cardholder should follow the link in the email to verify your registration with Amex.'
                }
                expressions={{
                  clickTheVerificationLink: (
                    <b>
                      <Trns source={'Click the verification link'} />
                    </b>
                  ),
                }}
              />,
            ]}
            takeNoteBullets={[
              <div key={0}>{emailDeliveryMessage}</div>,
              <div key={1}>{dontKnowYourAmexLoginMessage}</div>,
              <div key={2}>
                <Trns
                  source={
                    'Not sure how to determine the primary cardholder? View our ${resourceCenter} for detailed instructions'
                  }
                  expressions={{
                    resourceCenter: (
                      <ResourceCenterLink
                        size={HoverableLinkSizes.Small}
                        underline
                        regular
                        analytics={{
                          event:
                            'account detail - alert component helpcenter link - text link press',
                          properties: {
                            interaction: 'text link press',
                            intent: 'alert component helpcenter link',
                            screen: accountOverviewUrl(':id'),
                          },
                        }}
                      />
                    ),
                  }}
                />
              </div>,
            ]}
          />
        )
      }
    }
  }

  return (
    <Box
      css={css`
        align-items: center;
        align-self: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 500px;
      `}
    >
      <SvgStatusSummaryActionRequiredRed />
      <div
        css={css`
          display: flex;
          align-items: center;
          margin-top: 10px;
          ${getFontCss(
            16,
            styleConstants.colors.red_dark,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        <h3>
          <Trns source={'Action Required'} />
        </h3>
      </div>
      <div>{innerContent(props.creditCard)}</div>
    </Box>
  )
}

export default AmexAccountVerificationActionRequiredBox
